import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { getCoupons, createCoupon, updateCoupon, deleteCoupon } from '../../services/couponApi';
import toast from 'react-hot-toast';
import {
  Plus, Edit2, Trash2, X, Save, RefreshCw, Tag, Search,
  Sparkles, CheckCircle2, TrendingUp, Clock, ShieldCheck,
  Loader2, BadgeCheck, ArrowUpRight
} from 'lucide-react';

const emptyPackage = {
  name: '',
  description: '',
  discountPercentage: '',
  taxType: 'none',
  taxPercentage: '',
  taxName: '',
  isActive: true
};

const CouponList = () => {
  const { user } = useAuth();
  const [coupons, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyPackage);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const canManage = ['admin', 'customer_care', 'cc_head'].includes(user?.role);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await getCoupons({ isActive: 'all' });
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setPackages(list);
    } catch (error) {
      console.error('Failed to load coupons:', error);
      toast.error('Failed to load coupons');
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const filteredPackages = coupons.filter(coupon =>
    coupon.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coupon.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activePackages = coupons.filter(p => p.isActive).length;
  const totalPackages = coupons.length;

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyPackage);
    setShowModal(true);
  };

  const openEditModal = (coupon) => {
    setEditingId(coupon._id);
    setFormData({
      name: coupon.name || '',
      description: coupon.description || '',
      discountPercentage: coupon.discountPercentage ?? 0,
      taxType: coupon.taxType || 'none',
      taxPercentage: coupon.taxPercentage ?? 0,
      taxName: coupon.taxName || '',
      isActive: coupon.isActive ?? true
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(emptyPackage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || undefined,
        discountPercentage: formData.discountPercentage === '' ? 0 : Number(formData.discountPercentage),
        taxType: formData.taxType || 'none',
        taxPercentage: formData.taxPercentage ? Number(formData.taxPercentage) : 0,
        taxName: formData.taxName || undefined,
        isActive: Boolean(formData.isActive)
      };

      if (editingId) {
        await updateCoupon(editingId, payload);
        toast.success('Coupon updated successfully');
      } else {
        await createCoupon(payload);
        toast.success('Coupon created successfully');
      }

      closeModal();
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteCoupon(id);
      toast.success('Coupon deleted successfully');
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete coupon');
    } finally {
      setDeletingId(null);
    }
  };

  const hasDiscount = Number(formData.discountPercentage || 0) > 0;
  const hasTax = (formData.taxType || 'none') !== 'none' && Number(formData.taxPercentage || 0) > 0;

  const stats = [
    {
      label: 'Total Coupons',
      value: totalPackages,
      icon: <Tag size={20} />,
      color: 'from-[#143E78] to-blue-500',
      lightBg: 'bg-blue-50',
      textColor: 'text-[#143E78]'
    },
    {
      label: 'Active Now',
      value: activePackages,
      icon: <CheckCircle2 size={20} />,
      color: 'from-emerald-500 to-teal-500',
      lightBg: 'bg-emerald-50',
      textColor: 'text-emerald-600'
    },
    {
      label: 'Active Discounts',
      value: activePackages,
      icon: <TrendingUp size={20} />,
      color: 'from-amber-500 to-orange-500',
      lightBg: 'bg-amber-50',
      textColor: 'text-amber-600'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-10">
      {/* Premium Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Appointment Coupons</h1>
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest mt-1">
            Setup consultation coupons with pricing and discounts
          </p>
        </div>
        {canManage && (
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2f5e] transition-all shadow-lg shadow-[#143E78]/20 active:scale-95"
          >
            <Plus size={18} />
            New Coupon
          </button>
        )}
      </div>

      {/* Premium Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-6 flex items-center gap-4">
            <div className={`p-3 rounded-2xl ${stat.lightBg} ${stat.textColor}`}>
              {stat.icon}
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{stat.label}</p>
              <p className="text-2xl font-black text-slate-900 mt-0.5">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Premium Search Card */}
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search coupons by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
            />
          </div>
          <button
            onClick={fetchPackages}
            className="inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl hover:bg-slate-50 transition-all text-xs font-black uppercase tracking-wider"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Premium Coupons Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <Loader2 size={40} className="animate-spin text-[#143E78]" />
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Loading coupons...</p>
          </div>
        </div>
      ) : filteredPackages.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-16 text-center">
          <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-slate-200">
            <Tag size={32} className="text-slate-300" />
          </div>
          <p className="text-sm font-black text-slate-500 uppercase tracking-wider">No coupons found</p>
          <p className="text-xs font-bold text-slate-400 mt-1">Try adjusting your search or create a new coupon</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredPackages.map((coupon) => {
              const hasDiscount = coupon.discountPercentage > 0;
              const hasTax = coupon.taxType && coupon.taxType !== 'none' && coupon.taxPercentage > 0;
              const taxLabel = coupon.taxType === 'cgst_sgst' ? 'GST' : (coupon.taxName || 'Tax');

              return (
              <div
                key={coupon._id}
                className="group bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:border-[#143E78]/30 hover:shadow-2xl hover:shadow-[#143E78]/10 transition-all duration-300 overflow-hidden"
              >
                {/* Card Header */}
                <div className="p-6 pb-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-2xl ${
                        coupon.isActive
                          ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                          : 'bg-slate-100 text-slate-400 border border-slate-200'
                      }`}>
                        <Tag size={20} />
                      </div>
                      <div>
                        <h3 className="text-base font-black text-slate-900 tracking-tight">{coupon.name}</h3>
                        <span className={`inline-flex items-center gap-1 mt-1 px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider ${
                          coupon.isActive
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                            : 'bg-slate-100 text-slate-500 border border-slate-200'
                        }`}>
                          {coupon.isActive ? (
                            <>
                              <CheckCircle2 size={12} /> Active
                            </>
                          ) : (
                            <>
                              <Clock size={12} /> Inactive
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openEditModal(coupon)}
                          className="p-2 text-slate-400 hover:text-[#143E78] hover:bg-[#143E78]/10 rounded-xl transition-colors"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon._id)}
                          disabled={deletingId === coupon._id}
                          className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors disabled:opacity-50"
                        >
                          {deletingId === coupon._id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  {coupon.description && (
                    <p className="text-xs font-bold text-slate-500 leading-relaxed mb-4 line-clamp-2">
                      {coupon.description}
                    </p>
                  )}
                </div>

                {/* Pricing Section */}
                <div className="px-6 pb-4 space-y-3">
                  {hasDiscount && (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Discount</span>
                      <span className="text-sm font-black text-emerald-600">
                        {coupon.discountPercentage > 0 ? `(${coupon.discountPercentage}%)` : ''}
                      </span>
                    </div>
                  )}

                  {hasTax && (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{taxLabel}</span>
                      <span className="text-sm font-black text-amber-600">
                        +₹{(coupon.taxAmount || 0).toLocaleString()} ({coupon.taxPercentage}%)
                      </span>
                    </div>
                  )}

                  {!hasDiscount && !hasTax && (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Discount/Tax</span>
                      <span className="text-sm font-black text-slate-500">None</span>
                    </div>
                  )}
                </div>

                {/* Card Footer */}
                <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={14} className="text-slate-400" />
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                        Consultation Coupon
                      </span>
                    </div>
                    {canManage && (
                      <button
                        onClick={() => openEditModal(coupon)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-wider hover:border-[#143E78] hover:text-[#143E78] transition-all opacity-0 group-hover:opacity-100"
                      >
                        <Edit2 size={12} />
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Premium Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 pb-4 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-[#143E78]/10 rounded-2xl border border-[#143E78]/20">
                    <Tag size={20} className="text-[#143E78]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-slate-900 tracking-tight">
                      {editingId ? 'Edit Coupon' : 'New Coupon'}
                    </h3>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">
                      {editingId ? 'Update coupon details' : 'Create a new consultation coupon'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <X size={20} />
                </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                  <Tag size={14} className="text-[#143E78]" />
                  Package Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
                  placeholder="e.g., Standard Consultation"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                  <Sparkles size={14} className="text-amber-500" />
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none resize-none"
                  rows={2}
                  placeholder="Optional description..."
                />
              </div>

               <div className="space-y-2">
                 <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                   <ArrowUpRight size={14} className="text-rose-500" />
                   Discount Percentage (%)
                 </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.discountPercentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, discountPercentage: e.target.value }))}
                  className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
                  placeholder="0"
                />
               </div>

               <div className="space-y-2">
                 <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                   <span className="text-[#143E78]">%</span>
                   Tax Type
                 </label>
                 <select
                   value={formData.taxType}
                   onChange={(e) => setFormData(prev => ({ ...prev, taxType: e.target.value }))}
                   className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none appearance-none"
                 >
                   <option value="none">No Tax</option>
                   <option value="gst">GST</option>
                   <option value="cgst_sgst">CGST + SGST</option>
                   <option value="custom">Custom</option>
                 </select>
               </div>

               {(formData.taxType === 'gst' || formData.taxType === 'cgst_sgst') && (
                 <div className="space-y-2">
                   <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                     <span className="text-[#143E78]">%</span>
                     {formData.taxType === 'cgst_sgst' ? 'Total Tax Percentage (CGST + SGST)' : 'GST Percentage'}
                   </label>
                   <input
                     type="number"
                     min="0"
                     max="100"
                     step="0.1"
                     value={formData.taxPercentage}
                     onChange={(e) => setFormData(prev => ({ ...prev, taxPercentage: e.target.value }))}
                     className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
                     placeholder="18"
                   />
                 </div>
               )}

               {formData.taxType === 'custom' && (
                 <>
                   <div className="space-y-2">
                     <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                       <span className="text-[#143E78]">#</span>
                       Custom Tax Name
                     </label>
                     <input
                       type="text"
                       value={formData.taxName}
                       onChange={(e) => setFormData(prev => ({ ...prev, taxName: e.target.value }))}
                       className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
                       placeholder="e.g., Service Tax"
                     />
                   </div>
                   <div className="space-y-2">
                     <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                       <span className="text-[#143E78]">%</span>
                       Custom Tax Percentage
                     </label>
                     <input
                       type="number"
                       min="0"
                       max="100"
                       step="0.1"
                       value={formData.taxPercentage}
                       onChange={(e) => setFormData(prev => ({ ...prev, taxPercentage: e.target.value }))}
                       className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
                       placeholder="5"
                     />
                   </div>
                 </>
               )}

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-sm font-black text-slate-700 uppercase tracking-wider">Active Status</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#143E78]/10 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#143E78]"></div>
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-6 py-3.5 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl hover:bg-slate-50 transition-all text-xs font-black uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3.5 bg-[#143E78] text-white rounded-2xl hover:bg-[#0f2f5e] disabled:opacity-50 flex items-center justify-center gap-2 transition-all shadow-lg shadow-[#143E78]/20"
                >
                  {saving ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Save size={18} />
                  )}
                  <span className="text-xs font-black uppercase tracking-wider">{editingId ? 'Update' : 'Create'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponList;
